import React from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
} from '@mui/material';
import { LanguageConstants } from '../../helpers/LanguageConstants';
import { DATA_SOURCES_DIALOG_Z_INDEX } from '../../helpers/zIndexLayers';

interface Channel {
  id: string;
  name: string;
}

interface ChannelSelectDropdownProps {
  /** Current selected channel ID */
  value: string;
  /** Callback when channel selection changes */
  onChange: (value: string) => void;
  /** Callback when dropdown loses focus */
  onBlur?: () => void;
  /** Error message to display */
  error?: string;
  /** Whether the field is required (shows asterisk) */
  required?: boolean;
  /** Whether the dropdown is disabled */
  disabled?: boolean;
  /** Callback when dropdown opens */
  onOpen?: () => void;
  /** List of available channels */
  channels: Channel[];
  /** Whether channels are currently loading */
  loadingChannels: boolean;
  /** Error message from channels loading */
  channelsError: string;
}

/**
 * Reusable component for selecting a channel in the "Add Data Source" modal.
 * Displays a dropdown with available channels and handles loading/error states.
 */
const ChannelSelectDropdown: React.FC<ChannelSelectDropdownProps> = ({
  value,
  onChange,
  onBlur,
  error,
  required = true,
  disabled = false,
  onOpen,
  channels,
  loadingChannels,
  channelsError,
}) => {
  // MUI FormControl required={true} adds asterisk to InputLabel automatically; do not add manually
  return (
    <FormControl fullWidth error={Boolean(error)} required={required}>
      <InputLabel>Uploading to {LanguageConstants.topic}</InputLabel>
      <Select
        label={`Uploading to ${LanguageConstants.topic}`}
        value={value}
        onChange={(e) => {
          onChange(e.target.value as string);
        }}
        onBlur={onBlur}
        disabled={disabled || loadingChannels}
        onOpen={onOpen}
        MenuProps={{ sx: { zIndex: DATA_SOURCES_DIALOG_Z_INDEX + 10 } }}
      >
        <MenuItem value="">Select {LanguageConstants.topic.toLowerCase()}</MenuItem>
        {loadingChannels ? (
          <MenuItem disabled>
            <CircularProgress size={16} sx={{ mr: 1 }} />
            Loading {LanguageConstants.topic_s.toLowerCase()}...
          </MenuItem>
        ) : channelsError ? (
          <MenuItem disabled>Error loading {LanguageConstants.topic_s.toLowerCase()}</MenuItem>
        ) : (
          channels.map((channel) => (
            <MenuItem key={channel.id} value={channel.id}>
              {channel.name}
            </MenuItem>
          ))
        )}
      </Select>
      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
};

export default ChannelSelectDropdown;
